
import { Component, Vue } from 'vue-facing-decorator'
import { LockDec, ValidateDec } from '@/decorators';
import FieldList from './com/field-list.vue';
import TemplateDialog from './com/template-dialog.vue';
import { isOptionType } from '@/common/sign';

@Component({
  components: { FieldList, TemplateDialog }
})
export default class extends Vue {
  loading = false;
  submitLoading = false;

  formData = {
    name: "",
    description: "",
    isEnabled: 1,
    isPaid: 0,
    price: "" as any,
    maxCount: 0,
    isCheck: 0,
    rowList: [] as any[],
    content: [] as any[],
  }

  get mesureId() {
    return this.$route.query.id;
  }

  /** 「去装修」旁边的状态文案：模板只在弹窗里改，关了弹窗得能看出到底配没配 */
  get templateTips() {
    let count = (this.formData.content || []).length;
    return count ? `已配置 ${count} 个组件` : "还没配置渲染内容";
  }

  async created() {
    if (!this.mesureId) return;
    this.loading = true;
    await this.getData();
    this.loading = false;
  }

  async getData() {
    let { error, data } = await this.$post("/manager/sign/detail", {
      id: this.mesureId,
    });
    if (error) return;
    /**
     * tinyint 从库里出来可能是字符串，ui-switch 是严格比较（modelValue === trueValue），
     * 不归一化开关会静默显示成关；表头的 sort 不用带回来，提交时按数组顺序重排
     */
    this.formData = {
      name: data.name || "",
      description: data.description || "",
      isEnabled: data.isEnabled == 1 ? 1 : 0,
      isPaid: data.isPaid == 1 ? 1 : 0,
      price: data.price != null ? Number(data.price) : "",
      maxCount: Number(data.maxCount || 0),
      isCheck: data.isCheck == 1 ? 1 : 0,
      /** json 字段没配过时是 null，交给 ui-custom 会在它 created 里 .map 炸掉 */
      content: Array.isArray(data.content) ? data.content : [],
      rowList: (data.activityRowList || []).map((item) => {
        return {
          id: item.id,
          name: item.name || "",
          placeholder: item.placeholder || "",
          type: Number(item.type),
          isRequire: item.isRequire == 1 ? 1 : 0,
          isSystem: item.isSystem == 1 ? 1 : 0,
          content: item.content || null,
        };
      }),
    };
  }

  /**
   * 装修编辑器放全屏弹窗里：和店铺装修共用 ui-custom，能拖组件、能在手机预览里看效果。
   * 全屏还有个好处 —— 弹窗铺满视口就没有遮罩可点，不会点着点着把刚配好的模板点没了。
   */
  async editTemplate() {
    let result: any;
    try {
      result = await this.$dialog({
        title: "活动渲染模板",
        props: {
          fullscreen: true,
        },
        content: () => {
          return <TemplateDialog modelValue={this.formData.content} pageName={this.formData.name}></TemplateDialog>;
        },
      }, "sign-template");
    } catch (e) {
      /** 右上角关闭 = 取消，表单里原有的模板不动 */
      return;
    }
    /** 同一 key 的弹窗已经开着时 $dialog 直接 resolve [{ msg: '已唤起' }]，别把它当模板写回去 */
    if (!Array.isArray(result) || result.some((item) => !item || !item.render)) return;
    this.formData.content = result;
  }

  /** 关掉收费就把价格清掉，免得把上一次填的金额又带到免费活动上 */
  changeIsPaid(value) {
    this.formData.isPaid = value;
    if (value != 1) {
      this.formData.price = "";
    }
  }

  /** 本地这一层只做人能看懂的校验，字段长度、选项这些后端也会再挡一次 */
  validate() {
    if (!this.formData.rowList.length) return "请至少添加一个报名字段";
    for (let item of this.formData.rowList) {
      let name = `${item.name || ''}`.trim();
      if (!name) return "请输入字段名称";
      if (name.length > 25) return `「${name}」字段名称不能超过25个字`;
      if (`${item.placeholder || ''}`.length > 50) return `「${name}」输入提示不能超过50个字`;
      if (isOptionType(item.type)) {
        let list = (item.content?.list || []).filter((option) => option.name || option.value);
        if (!list.length) return `「${name}」请至少添加一个选项`;
      }
    }
    if (this.formData.isPaid == 1 && !(Number(this.formData.price) >= 0.01)) {
      return "请填写大于0的报名费";
    }
    return "";
  }

  @ValidateDec("form", { showTips: true })
  @LockDec("submitLoading")
  async submit() {
    let error = this.validate();
    /** 这里不能 throw：ValidateDec 会把方法体里的异常吞掉，只留一个没提示的失败 */
    if (error) return this.$error(error);

    let payload: any = {
      name: `${this.formData.name}`.trim(),
      description: this.formData.description,
      isEnabled: this.formData.isEnabled,
      isPaid: this.formData.isPaid,
      maxCount: Number(this.formData.maxCount) || 0,
      isCheck: this.formData.isCheck,
      /**
       * 空数组也要传：不然清空模板后存不回库，老模板会一直留着。
       * 条目由 ui-custom 产出，只有 render / data（TemplateDataDto 就认这两个），
       * 内部的 uuid_ 是它的渲染 key，它的保存逻辑已经删掉了。
       */
      content: this.formData.content,
      /**
       * 逐个字段挑出来，不 spread 整行：表头行里还有 activityId / sort，
       * 传回去会被后端当成越权字段；已有字段必须带 id，否则后端把它当新字段、删旧建新
       */
      rowList: this.formData.rowList.map((item, index) => {
        let row: any = {
          name: `${item.name}`.trim(),
          type: item.type,
          isRequire: item.isRequire,
          sort: index,
        };
        if (item.id != null && item.id !== "") {
          row.id = item.id;
        }
        if (item.placeholder) {
          row.placeholder = item.placeholder;
        }
        if (item.content != null) {
          row.content = item.content;
        }
        if (item.isSystem == 1) {
          row.isSystem = 1;
        }
        return row;
      }),
    };
    if (this.mesureId) {
      payload.id = this.mesureId;
    }
    /** 免费活动不能带 price：SignOperatDto 只在 isPaid=1 时校验它，带 0 反而是非法值 */
    if (this.formData.isPaid == 1) {
      payload.price = Number(this.formData.price);
    }

    await this.$postNotify("/manager/sign/operat", payload);
    this.$router.back();
  }
}
